/**
 * src/pages/reports/_panelAdapters.js — Overview payloads → chart shapes.
 * Card P242 (B2a).
 *
 * ⭐ ADAPTERS, NOT NEW CHARTS. CeoCharts.js already draws a stage bar and an
 * aging bar — they were built for the AI report (P201) and carry P228/P231's
 * legibility rules: top 10 plus an "Other" CAPTION, per-row axis sizing, no
 * overlapping labels. Writing a second pair for the Overview would be two
 * answers to one question, which is the shape this repo keeps paying for.
 *
 * ⛔⛔ SO THE WORK IS UNITS, AND UNITS ARE WHERE THIS GOES WRONG.
 * The Overview's payloads speak CENTS (`totalCents`, `outstandingCents`).
 * The AI-report charts speak DOLLARS (`open_value` is SUM(o.value), and
 * crm_opportunities.value is dollars). Passing one to the other unconverted
 * draws a bar one hundredth — or one hundred times — its true length, in a
 * chart that looks entirely plausible. P220 hit exactly this with a target
 * line; the conversion is pinned by a test here for the same reason.
 */

/**
 * ⚠ A placeholder meaning "this payload does not carry funnel mapping", as
 * distinct from "this stage has none". Truthy, so no bar is captioned as
 * unmapped on a payload that cannot know.
 */
export const MAPPING_NOT_SUPPLIED = 'not-supplied';

/** Cents → dollars, for a chart whose axis is drawn in dollars. */
export function centsToDollars(cents) {
  const n = Number(cents);
  return Number.isFinite(n) ? n / 100 : 0;
}

/**
 * "Where the open pipeline sits — top stages" → BarByStage rows.
 *
 * ⚠ A NULL stage is a real group — opportunities with no stage set — and is
 * NAMED, never dropped and never rendered blank. P179 removed a `.slice(0,6)`
 * here for the same reason: showing six of forty with nothing saying so.
 * Folding to "top 10 + a caption" is BarByStage's job, not this adapter's.
 */
export function stagesToBarRows(stages) {
  return (Array.isArray(stages) ? stages : []).map((g) => ({
    stage: g.key === null || g.key === undefined || g.key === ''
      ? '(no stage set)' : String(g.key),
    /* ⛔ DOLLARS. The source field is `totalCents`. */
    open_value: centsToDollars(g.totalCents),
    open_opps: Number(g.n) || 0,
    /*
     * ⛔⛔ MAPPING IS UNKNOWN HERE, WHICH IS NOT THE SAME AS UNMAPPED.
     *
     * BarByStage greys a bar and captions it "no funnel mapping" when
     * `funnel_bucket` is absent — a claim it can make safely about the AI
     * report's payload, which carries that field. The Overview's stage rows
     * are `{key, n, totalCents}` from the drill-down resolver and carry NO
     * funnel mapping at all, so an absent field here means "not supplied",
     * not "not mapped".
     *
     * ⚠ Found in the evidence PNG: every bar rendered grey under the caption
     * "10 stage(s) in grey have no funnel mapping" — a confident, false
     * statement about every stage in the pipeline.
     *
     * ⚠ The honest fix is a payload change (carry funnel_bucket on the
     * Overview's stage rows); that is listed in the PR, not smuggled in
     * here. Until then the adapter declines to make the claim.
     */
    funnel_bucket: MAPPING_NOT_SUPPLIED,
  }));
}

/**
 * "Receivables" aging rows → the ReceivablesAging block.
 *
 * ⚠ `order` is carried explicitly so the bands stay 1–30, 31–60, 61–90, 90+.
 * Sorting them by value would put the biggest band first and destroy the one
 * thing an aging chart is for: age runs in one direction.
 *
 * ⚠ This component reads CENTS (`cents`), unlike BarByStage — so the rows
 * pass through unconverted, and the test pins both directions.
 */
export function agingToBuckets(agingRows) {
  const rows = Array.isArray(agingRows) ? agingRows : [];
  if (!rows.length) return null;
  /*
   * ⛔⛔ THE KEY IS CANONICAL, THE LABEL IS THE WORDS — and that distinction
   * is load-bearing. ReceivablesAging colours its bars by KEY
   * (`r.key === 'd90_plus'` → red, `'d31_90'` → amber): a severity ramp, not
   * the categorical palette. Passing the display name as the key renders
   * every band the same blue, so "90+ days" stops looking urgent.
   *
   * ⚠ Found in the evidence PNG, not in review — the chart drew correctly
   * ordered, correctly sized, uniformly blue bars, which looks like a
   * finished chart rather than a broken one.
   */
  const KEY_FOR = {
    '1–30 days': 'd1_30',
    '31–60 days': 'd31_60',
    '61–90 days': 'd61_90',
    '90+ days': 'd90_plus',
  };
  const byBucket = {};
  const order = [];
  for (const r of rows) {
    const band = String(r.band || '');
    if (!band) continue;
    const key = KEY_FOR[band] || band;
    order.push(key);
    byBucket[key] = {
      label: band,
      cents: Number(r.outstandingCents) || 0,
      count: Number(r.n) || 0,
    };
  }
  return order.length ? { byBucket, order } : null;
}


/**
 * Invoice buckets → a 100% stacked bar — Card P242 B2b.
 *
 * ⚠ ONE BAR, SHARES OF A WHOLE. The panel's donut answered "how many of
 * each"; a 100% bar answers "what proportion is in trouble", which is the
 * question a CEO asks of invoice status. Both are legitimate; the card picks
 * this one, and ≤5 categories keeps it readable.
 *
 * ⛔ THE TOTAL IS THE DENOMINATOR, and it is returned so the caller can print
 * it. A percentage with no base is unreadable: "32% overdue" of eleven
 * invoices and of eleven hundred are different facts.
 */
export function invoiceBucketsToStack(donutRows) {
  const rows = (Array.isArray(donutRows) ? donutRows : [])
    .filter((d) => Number(d.value) > 0);
  const total = rows.reduce((a, d) => a + (Number(d.value) || 0), 0);
  if (!total) return null;
  return {
    total,
    /* ⚠ One datum: recharts stacks SERIES across one row, so each bucket is
       a key on a single object, not a row of its own. */
    datum: rows.reduce((o, d) => Object.assign(o, { [d.key]: Number(d.value) || 0 }), {}),
    series: rows.map((d) => ({
      key: d.key,
      name: d.name,
      value: Number(d.value) || 0,
      /* ⛔ Rounded for DISPLAY only — never used to re-derive a count. */
      pct: Math.round((Number(d.value) / total) * 1000) / 10,
      fill: d.fill,
    })),
  };
}

/**
 * Lead sources → chart rows — Card P242 B2b, item 11.
 *
 * ⛔⛔ NO GROWTH PERCENTAGE IS COMPUTED HERE. The payload carries
 * `previousCount`, and it is tempting to divide. P237 is explicit that a
 * comparison must state its window and that a suppressed percentage must
 * give its reason — which is what the metric envelope and the comparability
 * helpers exist for. A raw count pair cannot say whether the windows are
 * comparable, so this adapter carries the previous COUNT through untouched
 * and leaves the judgement to the caller.
 *
 * ⚠ TOP N PLUS "Other", folded HERE rather than in the chart, because a
 * donut's slice budget is a property of the donut: P222 caps categorical
 * series at six, and a seventh slice must collapse rather than borrow a hue.
 */
export function leadSourcesToRows(sources, maxSlices = 6) {
  const rows = (Array.isArray(sources) ? sources : [])
    .map((r) => ({
      source: r.source || 'Unknown',
      count: Number(r.count) || 0,
      previousCount: r.previousCount === null || r.previousCount === undefined
        ? null : Number(r.previousCount),
    }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
  if (!rows.length) return null;

  const total = rows.reduce((a, r) => a + r.count, 0);
  if (rows.length <= maxSlices) return { rows, total, folded: 0, foldedCount: 0 };

  const shown = rows.slice(0, maxSlices - 1);
  const rest = rows.slice(maxSlices - 1);
  const restCount = rest.reduce((a, r) => a + r.count, 0);
  return {
    /* ⚠ "Other" carries its MEMBER COUNT so the reader knows what collapsed. */
    rows: [...shown, { source: `Other (${rest.length})`, count: restCount,
                       previousCount: null, isOther: true }],
    total,
    folded: rest.length,
    foldedCount: restCount,
  };
}
